/**
 * State for the "tap N times to unlock dev mode" gesture. Each successful
 * click advances `count` until it reaches `target`, at which point the caller
 * should toggle dev mode and reset. A gap longer than `windowMs` between
 * clicks restarts the count.
 */
export interface DevModeClickState {
  count: number
  /** Timestamp (ms) of the most recent click counted toward the current streak. */
  lastClickAt: number
}

export const INITIAL_DEV_MODE_CLICK_STATE: DevModeClickState = {
  count: 0,
  lastClickAt: 0
}

export interface DevModeClickResult {
  state: DevModeClickState
  shouldToggle: boolean
}

/**
 * Given the current click state and the timestamp of a new click, return the
 * next state and whether the caller should toggle dev mode.
 *
 * - First click (or first click after the window expired) restarts the streak.
 * - Reaching `target` resets to the initial state and signals a toggle.
 *
 * Pure function — no side effects, no time of its own.
 */
export function nextDevModeClickState(
  current: DevModeClickState,
  now: number,
  windowMs = 3000,
  target = 6
): DevModeClickResult {
  const isFirstOrExpired = current.lastClickAt === 0 || now - current.lastClickAt > windowMs
  const nextCount = isFirstOrExpired ? 1 : current.count + 1

  if (nextCount >= target) {
    return { state: INITIAL_DEV_MODE_CLICK_STATE, shouldToggle: true }
  }
  return { state: { count: nextCount, lastClickAt: now }, shouldToggle: false }
}
